import { useAuth } from "@/components/AuthContext";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Settings, Shield, LayoutDashboard, BookOpen, ShoppingBag } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const LOGIN_URL =
  `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth-callback")}`;

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function ProfileMenu() {
  const { user, loading, logout, isSuperAdmin, isAdmin, isApostle, isStaff, isHost, hostedCollectives } = useAuth();
  const navigate = useNavigate();

  if (loading) return null;

  if (!user) {
    return (
      <a
        href={LOGIN_URL}
        data-testid="header-sign-in-btn"
        className="flex items-center text-white/80 hover:text-gold transition-colors"
      >
        <User className="h-7 w-7" />
      </a>
    );
  }

  const handleSignOut = async () => {
    await logout();
    navigate("/");
  };

  const roleBadge = isApostle
    ? "Apostle"
    : isSuperAdmin
    ? "IT Admin"
    : isAdmin
    ? "Admin"
    : isStaff
    ? "Staff"
    : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild data-testid="profile-menu-trigger">
        <button className="rounded-full ring-2 ring-gold/40 hover:ring-gold transition-all focus:outline-none">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.picture} alt={user.name} referrerPolicy="no-referrer" />
            <AvatarFallback className="bg-gold/20 text-gold text-xs font-bold">
              {getInitials(user.display_name || user.name)}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56 bg-navy border-gold/20 text-white">
        <DropdownMenuLabel className="font-normal" data-testid="profile-menu-user-info">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-white truncate">
              {user.display_name || user.name}
            </p>
            <p className="text-xs text-white/50 truncate">{user.email}</p>
            {roleBadge && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gold font-semibold mt-0.5">
                <Shield className="h-3 w-3" /> {roleBadge}
              </span>
            )}
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="bg-white/10" />

        {/* My Profile — everyone */}
        <DropdownMenuItem
          data-testid="profile-menu-my-profile"
          className="cursor-pointer text-white/80 hover:text-white focus:text-white focus:bg-white/10"
          onClick={() => navigate("/me")}
        >
          <Settings className="h-4 w-4 mr-2" /> My Profile
        </DropdownMenuItem>

        {/* Guide Dashboard — hosts only (guide pair leaders) */}
        {isHost && hostedCollectives.length > 0 && (
          <DropdownMenuItem
            data-testid="profile-menu-guide-dash"
            className="cursor-pointer text-white/80 hover:text-white focus:text-white focus:bg-white/10"
            onClick={() => navigate(`/host/${hostedCollectives[0].slug}`)}
          >
            <LayoutDashboard className="h-4 w-4 mr-2" /> Guide Dashboard
          </DropdownMenuItem>
        )}

        {/* Apostle's Den — apostle only */}
        {isApostle && (
          <DropdownMenuItem
            data-testid="profile-menu-den"
            className="cursor-pointer text-[#D4AF37]/90 hover:text-[#D4AF37] focus:text-[#D4AF37] focus:bg-[#D4AF37]/10"
            onClick={() => navigate("/den")}
          >
            <BookOpen className="h-4 w-4 mr-2" /> Apostle's Den
          </DropdownMenuItem>
        )}

        {/* Apostolic Dashboard — apostle, it_admin, admin (NOT staff) */}
        {isAdmin && (
          <DropdownMenuItem
            data-testid="profile-menu-apostolic-dash"
            className="cursor-pointer text-white/80 hover:text-white focus:text-white focus:bg-white/10"
            onClick={() => navigate("/dashboard")}
          >
            <LayoutDashboard className="h-4 w-4 mr-2" /> Apostolic Dashboard
          </DropdownMenuItem>
        )}

        {/* Ops Center — apostle, it_admin, admin, staff */}
        {(isAdmin || isStaff) && (
          <DropdownMenuItem
            data-testid="profile-menu-admin-dash"
            className="cursor-pointer text-white/80 hover:text-white focus:text-white focus:bg-white/10"
            onClick={() => navigate("/wa-ops-center")}
          >
            <ShoppingBag className="h-4 w-4 mr-2" /> Ops Center
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className="bg-white/10" />

        <DropdownMenuItem
          data-testid="profile-menu-sign-out"
          className="cursor-pointer text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-red-500/10"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
